import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { usePomodoro, fmtTime } from '../../hooks/usePomodoro';

// Classic Pomodoro: 25-minute focus + 5-minute break, auto-transitioning, with a chime.
// The timer is server-authoritative and synced over Socket.io (see usePomodoro), so it can be
// controlled from multiple screens at once (e.g. laptop + office TV) with identical progress.
const Pomodoro = ({ focusTask }) => {
  const { connected, isFocus, running, secondsLeft, pct, completed, focusTask: syncedFocus, send, setFocus } = usePomodoro();

  // Push the locally-selected focus task up so other screens show it too.
  useEffect(() => { if (focusTask) setFocus(focusTask); }, [focusTask, setFocus]);

  const displayFocus = syncedFocus || focusTask;

  return (
    <div className="flex flex-col items-center bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 sticky top-6">
      <div className="flex items-center gap-2 mb-1">
        <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full ${isFocus ? 'bg-primary-blue/10 text-primary-blue' : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'}`}>
          {isFocus ? 'Focus' : 'Break'}
        </span>
        {!connected && (
          <span className="text-[10px] font-bold uppercase tracking-widest text-amber-500">Connecting…</span>
        )}
      </div>

      {/* Circular progress */}
      <div className="relative my-4">
        <svg className="w-44 h-44 -rotate-90" viewBox="0 0 120 120">
          <circle cx="60" cy="60" r="52" fill="none" strokeWidth="10"
            className="stroke-gray-100 dark:stroke-gray-800" />
          <circle cx="60" cy="60" r="52" fill="none" strokeWidth="10" strokeLinecap="round"
            className={isFocus ? 'stroke-primary-blue' : 'stroke-emerald-500'}
            strokeDasharray={2 * Math.PI * 52}
            strokeDashoffset={(2 * Math.PI * 52) * (1 - pct / 100)}
            style={{ transition: 'stroke-dashoffset 1s linear' }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-4xl font-black tabular-nums text-gray-900 dark:text-gray-100">{fmtTime(secondsLeft)}</span>
          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 mt-1">{completed} done</span>
        </div>
      </div>

      {/* Current focus target */}
      <div className="w-full mb-4 text-center min-h-[2.5rem]">
        {displayFocus ? (
          <p className="text-sm font-bold text-gray-700 dark:text-gray-200 line-clamp-2">{displayFocus}</p>
        ) : (
          <p className="text-xs text-gray-400 italic">Select a slot to focus on it</p>
        )}
      </div>

      {/* Controls */}
      <div className="flex gap-2 w-full">
        <button
          onClick={() => send(running ? 'pause' : 'start')}
          disabled={!connected}
          className={`flex-1 py-2.5 rounded-xl font-black uppercase tracking-widest text-xs text-white transition-all disabled:opacity-40 ${running ? 'bg-amber-500 hover:bg-amber-600' : 'bg-primary-blue hover:opacity-90'}`}>
          {running ? 'Pause' : 'Start'}
        </button>
        <button
          onClick={() => send('reset')}
          disabled={!connected}
          className="px-4 py-2.5 rounded-xl font-black uppercase tracking-widest text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-all disabled:opacity-40">
          Reset
        </button>
      </div>
      <button
        onClick={() => send('skip')}
        disabled={!connected}
        className="mt-2 text-[10px] font-bold uppercase tracking-widest text-gray-400 hover:text-primary-blue transition-colors disabled:opacity-40">
        Skip to {isFocus ? 'break' : 'focus'}
      </button>

      <Link
        to="/tv"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-4 text-[10px] font-bold uppercase tracking-widest text-gray-300 dark:text-gray-600 hover:text-primary-blue transition-colors">
        📺 Open TV mode
      </Link>
    </div>
  );
};

export default Pomodoro;
